/**
 * Data Browser hooks (GH #1824).
 *
 * React Query hooks over the typed dataBrowserClient for the operator-scoped,
 * IP-allowlisted, audited read-only data browser + the ephemeral paste-able
 * token mint/revoke. Types derived from admin-data-browser.openapi.yaml via
 * @aaimplatform/institutional-types.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dataBrowserClient } from '../api/typed-clients';
import type { components } from '@aaimplatform/institutional-types/admin-data-browser';

export type DatabaseKey = components['schemas']['DatabaseKey'];
export type DatabaseInfo = components['schemas']['DatabaseInfo'];
export type DatabaseList = components['schemas']['DatabaseList'];
export type TableInfo = components['schemas']['TableInfo'];
export type TableList = components['schemas']['TableList'];
export type ColumnInfo = components['schemas']['ColumnInfo'];
export type ColumnList = components['schemas']['ColumnList'];
export type RowList = components['schemas']['RowList'];
export type QueryResponse = components['schemas']['QueryResponse'];
export type TokenMintResponse = components['schemas']['TokenMintResponse'];
export type DataRow = components['schemas']['DataRow'];
export type DataBrowserViewTab = components['schemas']['DataBrowserViewTab'];
export type DataBrowserRowsQuery = components['schemas']['DataBrowserRowsQuery'];

const KEY = 'data-browser';

function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error || !result.data) {
    const status = result.response?.status ?? 0;
    const statusText = result.response?.statusText ?? 'Unknown';
    const errorBody = result.error as { error?: { message?: string; code?: string } } | undefined;
    throw new Error(errorBody?.error?.message ?? `HTTP ${status}: ${statusText}`);
  }
  return result.data;
}

export function useDatabases() {
  return useQuery({
    queryKey: [KEY, 'databases'],
    queryFn: async (): Promise<DatabaseList> =>
      unwrap(await dataBrowserClient.GET('/api/v1/admin/data-browser/databases')),
    staleTime: 5 * 60_000,
  });
}

export function useTables(db: DatabaseKey | undefined, schema?: string) {
  return useQuery({
    queryKey: [KEY, 'tables', db, schema ?? null],
    enabled: !!db,
    queryFn: async (): Promise<TableList> =>
      unwrap(
        await dataBrowserClient.GET('/api/v1/admin/data-browser/tables', {
          params: { query: { db: db as DatabaseKey, schema, limit: 1000 } },
        }),
      ),
  });
}

export function useColumns(
  db: DatabaseKey | undefined,
  schema: string | undefined,
  table: string | undefined,
) {
  return useQuery({
    queryKey: [KEY, 'columns', db, schema ?? null, table ?? null],
    enabled: !!db && !!schema && !!table,
    queryFn: async (): Promise<ColumnList> =>
      unwrap(
        await dataBrowserClient.GET('/api/v1/admin/data-browser/columns', {
          params: { query: { db: db as DatabaseKey, schema: schema as string, table: table as string } },
        }),
      ),
  });
}

export function useRows(args: DataBrowserRowsQuery) {
  const { db, schema, table, page, limit, orderBy, orderDir } = args;
  return useQuery({
    queryKey: [KEY, 'rows', db, schema ?? null, table ?? null, page, limit, orderBy ?? null, orderDir ?? null],
    enabled: !!db && !!schema && !!table,
    queryFn: async (): Promise<RowList> =>
      unwrap(
        await dataBrowserClient.GET('/api/v1/admin/data-browser/rows', {
          params: {
            query: {
              db: db as DatabaseKey,
              schema: schema as string,
              table: table as string,
              page,
              limit,
              orderBy,
              orderDir,
            },
          },
        }),
      ),
  });
}

export function useRunQuery() {
  return useMutation({
    mutationFn: async (input: { db: DatabaseKey; sql: string }): Promise<QueryResponse> =>
      unwrap(await dataBrowserClient.POST('/api/v1/admin/data-browser/query', { body: input })),
  });
}

export function useMintToken() {
  // The generated TokenMintRequest types ttlMinutes as required, so always send it.
  return useMutation({
    mutationFn: async (input: { ttlMinutes: number }): Promise<TokenMintResponse> =>
      unwrap(await dataBrowserClient.POST('/api/v1/admin/data-browser/token', { body: input })),
  });
}

export function useRevokeToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { jti: string }): Promise<void> => {
      const result = await dataBrowserClient.DELETE('/api/v1/admin/data-browser/token', { body: input });
      if (result.error) {
        const errorBody = result.error as { error?: { message?: string } } | undefined;
        throw new Error(errorBody?.error?.message ?? `HTTP ${result.response?.status ?? 0}`);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY] });
    },
  });
}
